import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useUserOrders, useUserRentals } from '../hooks/useAPI';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  User, Mail, Phone, LogOut, Package, 
  Clock, MapPin, ChevronRight, Wind, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const ProfilePage = () => {
  const { user, token, logout } = useAuth();
  const { t, language } = useLanguage();
  const { orders, isLoading: loadingOrders, refresh: refreshOrders } = useUserOrders(token, !!user);
  const { rentals, isLoading: loadingRentals, refresh: refreshRentals } = useUserRentals(token, !!user);

  const loading = loadingOrders || loadingRentals;

  const handleRefresh = () => {
    refreshOrders();
    refreshRentals();
    toast.success(language === 'pt' ? 'Dados atualizados!' : 'Data refreshed!');
  };

  const getStatusColor = (status) => {
    const colors = {
      received: 'bg-yellow-100 text-yellow-800',
      preparing: 'bg-blue-100 text-blue-800',
      ready: 'bg-purple-100 text-purple-800',
      out_for_delivery: 'bg-orange-100 text-orange-800',
      delivered: 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      received: language === 'pt' ? 'Recebido' : 'Received',
      preparing: language === 'pt' ? 'Preparando' : 'Preparing',
      ready: language === 'pt' ? 'Pronto' : 'Ready',
      out_for_delivery: language === 'pt' ? 'Em Entrega' : 'Out for Delivery',
      delivered: language === 'pt' ? 'Entregue' : 'Delivered'
    };
    return labels[status] || status;
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#004E64] mb-4">
            {language === 'pt' ? 'Faça login para ver seu perfil' : 'Login to view your profile'}
          </h2>
          <Link to="/login">
            <Button className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full">
              {t('login')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Profile Card */}
        <Card className="rounded-3xl border-[#004E64]/5 mb-8">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-[#FF6B35] flex items-center justify-center text-white text-3xl font-bold">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full rounded-full object-cover" />
                ) : (
                  user.name?.charAt(0).toUpperCase()
                )}
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-[#004E64]" data-testid="user-name">{user.name}</h1>
                <div className="flex flex-wrap items-center gap-4 text-[#9FA2B4] mt-2">
                  <span className="flex items-center gap-1">
                    <Mail size={16} />
                    {user.email}
                  </span>
                  {user.phone && (
                    <span className="flex items-center gap-1">
                      <Phone size={16} />
                      {user.phone}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  className="rounded-full text-[#004E64] border-[#004E64]/10"
                  data-testid="refresh-orders-button"
                >
                  <RefreshCw size={16} className="mr-2" />
                  {language === 'pt' ? 'Atualizar' : 'Refresh'}
                </Button>
                <Button
                  variant="outline"
                  onClick={logout}
                  className="rounded-full text-red-500 border-red-200 hover:bg-red-50"
                >
                  <LogOut size={18} className="mr-2" />
                  {t('logout')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Orders & Rentals */}
        <Tabs defaultValue="orders" className="w-full">
          <TabsList className="w-full bg-[#F4F1EA] rounded-full p-1 mb-6">
            <TabsTrigger 
              value="orders" 
              className="flex-1 rounded-full data-[state=active]:bg-white data-[state=active]:text-[#FF6B35]"
            >
              <Package size={18} className="mr-2" />
              {t('orders')} ({orders.length})
            </TabsTrigger>
            <TabsTrigger 
              value="rentals"
              className="flex-1 rounded-full data-[state=active]:bg-white data-[state=active]:text-[#FF6B35]"
            >
              <Wind size={18} className="mr-2" />
              {t('shisha')} ({rentals.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="orders">
            {loading ? (
              <div className="space-y-4">
                {Array(3).fill(0).map((_, i) => (
                  <Skeleton key={i} className="h-24 rounded-2xl" />
                ))}
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12">
                <Package size={48} className="mx-auto text-[#9FA2B4] mb-4" />
                <p className="text-[#9FA2B4]">
                  {language === 'pt' ? 'Nenhum pedido ainda' : 'No orders yet'}
                </p>
                <Link to="/restaurants">
                  <Button className="mt-4 bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full">
                    {t('viewRestaurants')}
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map(order => (
                  <Link key={order.order_id} to={`/order-tracking/${order.order_id}`}>
                    <Card className="rounded-2xl border-[#004E64]/5 hover:shadow-md transition-all cursor-pointer">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="font-bold text-[#004E64]">#{order.order_id}</span>
                              <Badge className={getStatusColor(order.status)}>
                                {getStatusLabel(order.status)}
                              </Badge>
                            </div>
                            <p className="text-sm text-[#9FA2B4] mb-1">
                              {order.items.map(i => `${i.quantity}x ${i.name}`).join(', ')}
                            </p>
                            <div className="flex items-center gap-4 text-sm text-[#9FA2B4]">
                              <span className="flex items-center gap-1">
                                <MapPin size={14} />
                                {order.delivery_location}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock size={14} />
                                {new Date(order.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-[#FF6B35]">€{order.total.toFixed(2)}</p>
                            <ChevronRight className="text-[#9FA2B4] mt-2" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="rentals">
            {loading ? (
              <div className="space-y-4">
                {Array(3).fill(0).map((_, i) => (
                  <Skeleton key={i} className="h-24 rounded-2xl" />
                ))}
              </div>
            ) : rentals.length === 0 ? (
              <div className="text-center py-12">
                <Wind size={48} className="mx-auto text-[#9FA2B4] mb-4" />
                <p className="text-[#9FA2B4]">
                  {language === 'pt' ? 'Nenhum aluguer ainda' : 'No rentals yet'}
                </p>
                <Link to="/shisha">
                  <Button className="mt-4 bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full">
                    {t('rentShisha')}
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {rentals.map(rental => (
                  <Card key={rental.rental_id} className="rounded-2xl border-[#004E64]/5">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-bold text-[#004E64]">{rental.shisha_name}</span>
                            <Badge className={getStatusColor(rental.status)}>
                              {getStatusLabel(rental.status)}
                            </Badge>
                          </div>
                          <p className="text-sm text-[#9FA2B4] mb-1">
                            {rental.flavor_name} • {rental.duration_hours} {t('hours')}
                          </p>
                          <div className="flex items-center gap-4 text-sm text-[#9FA2B4]">
                            <span className="flex items-center gap-1">
                              <MapPin size={14} />
                              {rental.delivery_location}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {rental.delivery_time}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-[#FF6B35]">€{rental.total.toFixed(2)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ProfilePage;
